import { FormCategory } from "@simplyk/common";

import { getCookieByName } from "@/common/helpers/cookie";
import { RegisterCategory } from "@/features/Auth/Register/types";

type CookiePageType = "donation" | "event" | "raffle" | "auction" | "membership" | "eshop" | "peertopeer";

export class RegisterCategoryResolver {
  static resolveCategory(searchParams: URLSearchParams): RegisterCategory {
    const currentCategory = this.extractCategoryFromSearchParams(searchParams);
    if (currentCategory) {
      return currentCategory;
    }

    if (typeof document !== "undefined" && document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        const referrerCategory = this.extractCategoryFromSearchParams(referrerUrl.searchParams);
        if (referrerCategory) {
          return referrerCategory;
        }
      } catch {
        // Invalid referrer URL, ignore
      }
    }

    const cookieCategory = this.extractCategoryFromCookie();
    if (cookieCategory) {
      return cookieCategory;
    }

    return "donation";
  }

  private static extractCategoryFromSearchParams(searchParams: URLSearchParams): RegisterCategory | undefined {
    for (const [key, category] of Object.entries(this.QUERY_PARAM_TO_CATEGORY)) {
      if (searchParams.has(key)) {
        return category;
      }
    }
    return undefined;
  }

  private static extractCategoryFromCookie(): RegisterCategory | undefined {
    if (typeof document === "undefined") {
      return undefined;
    }

    const cookieValue = getCookieByName(this.PAGE_TYPES_COOKIE_NAME);
    if (!cookieValue) {
      return undefined;
    }

    const decodedCookieValue = this.decodeCookieValue(cookieValue);
    const parsedCookie = this.parsePageTypeCookie(decodedCookieValue) ?? this.parsePageTypeCookie(cookieValue);

    if (!parsedCookie) {
      return undefined;
    }

    for (const pageType of this.COOKIE_PAGE_TYPE_PRIORITY) {
      if ((parsedCookie[pageType] ?? 0) > 0) {
        return this.COOKIE_PAGE_TYPE_TO_CATEGORY[pageType];
      }
    }

    return undefined;
  }

  private static decodeCookieValue(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private static parsePageTypeCookie(value: string): Partial<Record<CookiePageType, number>> | undefined {
    try {
      const parsedValue = JSON.parse(value) as unknown;
      if (typeof parsedValue !== "object" || !parsedValue || Array.isArray(parsedValue)) {
        return undefined;
      }
      const record = parsedValue as Record<string, unknown>;
      const result: Partial<Record<CookiePageType, number>> = {};
      for (const [key, val] of Object.entries(record)) {
        const normalizedKey = key.toLowerCase();
        if (!this.VALID_COOKIE_PAGE_TYPES.has(normalizedKey)) {
          continue;
        }
        const numVal = Number(val);
        if (!Number.isNaN(numVal)) {
          result[normalizedKey as CookiePageType] = numVal;
        }
      }
      return Object.keys(result).length > 0 ? result : undefined;
    } catch {
      return undefined;
    }
  }

  static mapRegisterCategoryToFormCategory(category: RegisterCategory): FormCategory {
    return this.REGISTER_CATEGORY_TO_FORM_CATEGORY[category];
  }

  private static readonly QUERY_PARAM_TO_CATEGORY: Record<string, RegisterCategory> = {
    Event: "event",
    Donation: "donation",
    Raffle: "raffle",
    Auction: "auction",
    Membership: "membership",
    Shop: "shop",
    PeerToPeer: "peer-to-peer",
  };

  private static readonly PAGE_TYPES_COOKIE_NAME = "zeffy_page_types";

  private static readonly COOKIE_PAGE_TYPE_TO_CATEGORY: Record<CookiePageType, RegisterCategory> = {
    donation: "donation",
    event: "event",
    raffle: "raffle",
    auction: "auction",
    membership: "membership",
    eshop: "shop",
    peertopeer: "peer-to-peer",
  };

  private static readonly COOKIE_PAGE_TYPE_PRIORITY: CookiePageType[] = [
    "event",
    "raffle",
    "donation",
    "membership",
    "auction",
    "eshop",
    "peertopeer",
  ];

  private static readonly VALID_COOKIE_PAGE_TYPES = new Set<string>(this.COOKIE_PAGE_TYPE_PRIORITY);

  private static readonly REGISTER_CATEGORY_TO_FORM_CATEGORY: Record<RegisterCategory, FormCategory> = {
    event: FormCategory.Event,
    donation: FormCategory.DonationForm,
    raffle: FormCategory.Lottery,
    auction: FormCategory.Auction,
    membership: FormCategory.MembershipV2,
    shop: FormCategory.Shop,
    "peer-to-peer": FormCategory.PeerToPeerCampaign,
  };
}
